import type { ReactNode } from 'react';
import { Dropdown, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useMdUp } from '../../hooks/useLaboSukeMdUp';
import {
  openCompactHelp,
  openDetailedFilterToggle,
  openDockedColVisMenu,
  openDockedExportMenu,
} from '../../utils/sampleListExportDock';

export type SampleListChromeToolsProps = {
  toggleId: string;
  toggleClassName?: string;
  busy?: boolean;
  help?: ReactNode;
  menu: ReactNode;
  showExportSlot?: boolean;
  showHelpDock?: boolean;
  helpDockClassName?: string;
  exportSlotClassName?: string;
  toolsDropdownClassName?: string;
  /** true: ヘルプ/export を dock せず ⋯ に集約（狭い chrome） */
  compactChrome?: boolean;
  afterTools?: ReactNode;
};

type PageSelectorProps = { pageSelector: string };

export function ToolsMenuItem({
  iconClassName,
  label,
  title,
  onClick,
  disabled,
}: {
  iconClassName: string;
  label: string;
  /** 可視ラベルが短いとき用（未指定時は label） */
  title?: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Dropdown.Item onClick={onClick} disabled={disabled} title={title || label}>
      <i className={`${iconClassName} me-2`} aria-hidden="true" />
      {label}
    </Dropdown.Item>
  );
}

export function ToolsMenuDetailedFilter({ pageSelector }: PageSelectorProps) {
  const { t } = useTranslation();
  const label = t('sample_filter.detailed_filter');
  return (
    <Dropdown.Item
      title={label}
      onClick={() => {
        openDetailedFilterToggle(pageSelector);
      }}
    >
      <i className="fas fa-sliders-h me-2" aria-hidden="true" />
      {label}
    </Dropdown.Item>
  );
}

export function ToolsMenuHelp({ pageSelector }: PageSelectorProps) {
  const { t } = useTranslation();
  const label = t('common.help');
  const title = t('common.show_help', { defaultValue: label });
  return (
    <Dropdown.Item
      title={title}
      onClick={() => {
        openCompactHelp(pageSelector);
      }}
    >
      <i className="fas fa-question-circle me-2" aria-hidden="true" />
      {label}
    </Dropdown.Item>
  );
}

/** 列切替は md+ の表向け。xs カード一覧では出さない */
export function ToolsMenuColVis({ pageSelector }: PageSelectorProps) {
  const { t } = useTranslation();
  const isMdUp = useMdUp();
  if (!isMdUp) return null;
  const label = t('common.colvis');
  const title = t('common.colvis_title', { defaultValue: label });
  return (
    <Dropdown.Item
      title={title}
      onClick={() => {
        openDockedColVisMenu(pageSelector);
      }}
    >
      <i className="fas fa-columns me-2" aria-hidden="true" />
      {label}
    </Dropdown.Item>
  );
}

export function ToolsMenuExport({
  pageSelector,
  iconClassName = 'fas fa-file-export me-2',
}: PageSelectorProps & { iconClassName?: string }) {
  const { t } = useTranslation();
  // 入出力（Excel）と区別: 表示中テーブルのファイル出力
  const label = t('sample.table_export_short', { defaultValue: '一覧出力' });
  const title = t('sample.table_export_title', {
    defaultValue: '表示中の一覧をファイル出力',
  });
  return (
    <Dropdown.Item
      title={title}
      onClick={() => {
        openDockedExportMenu(pageSelector);
      }}
    >
      <i className={iconClassName} aria-hidden="true" />
      {label}
    </Dropdown.Item>
  );
}

/** ⋯ メニュー内の全ユーザー視点トグル */
export function ToolsMenuAdminViewToggle({
  checked,
  onChange,
  labelKey = 'storage_room.admin_view_all_label',
  hintKey = 'storage_room.admin_view_all_hint',
  inputId = 'tools-menu-admin-storage-view-all',
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  labelKey?: string;
  hintKey?: string;
  inputId?: string;
}) {
  const { t } = useTranslation();
  const label = t(labelKey);
  const hint = t(hintKey);
  return (
    <Dropdown.Item
      as="div"
      className="sample-tools-admin-view px-3 py-2"
      onClick={(e) => e.stopPropagation()}
    >
      <Form.Check
        type="switch"
        id={inputId}
        className="mb-0"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        label={label}
        title={hint}
      />
    </Dropdown.Item>
  );
}

export default function SampleListChromeTools({
  toggleId,
  toggleClassName = '',
  busy = false,
  help = null,
  menu,
  showExportSlot = true,
  showHelpDock = true,
  compactChrome = false,
  helpDockClassName,
  exportSlotClassName,
  toolsDropdownClassName,
  afterTools,
}: SampleListChromeToolsProps) {
  const { t } = useTranslation();
  const moreTitle = t('common.more_actions');
  const moreAria = t('common.actions');
  const helpCls =
    helpDockClassName ?? (compactChrome ? 'd-none' : 'd-none d-xl-block');
  const exportCls =
    exportSlotClassName ?? (compactChrome ? 'd-none' : 'd-none d-xl-block');
  const toolsCls =
    toolsDropdownClassName ??
    (compactChrome ? 'flex-shrink-0' : 'd-xl-none flex-shrink-0');

  return (
    <>
      {showHelpDock && help ? (
        <div className={`sample-list-help-dock ${helpCls}`.trim()}>{help}</div>
      ) : null}
      {showExportSlot ? (
        <div className={`sample-list-export-slot ${exportCls}`.trim()} aria-hidden="true" />
      ) : null}
      <Dropdown className={toolsCls}>
        <Dropdown.Toggle
          variant="outline-secondary"
          size="sm"
          className={`sample-chrome-icon-btn w-auto ${toggleClassName}`.trim()}
          disabled={busy}
          id={toggleId}
          title={moreTitle}
          aria-label={moreAria}
        >
          {busy ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <i className="fas fa-ellipsis-v" aria-hidden="true" />
          )}
        </Dropdown.Toggle>
        <Dropdown.Menu align="end" className="sample-list-tools-menu">
          {menu}
        </Dropdown.Menu>
      </Dropdown>
      {afterTools}
    </>
  );
}
